import { useId } from "react";
import { Icon } from "./icons";

type PagerProps = {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPage: (n: number) => void;
  /** Opt-in (listado de observaciones postventa): números de página con
   *  elipsis, «Anterior / Siguiente», `<nav>` y el meta «1–10 de 15». Sin
   *  esto el pager se pinta igual que siempre (facility, Tiendas). */
  numbered?: boolean;
  /** Opt-in: selector «Filas por página» con estos tamaños. */
  pageSizes?: readonly number[];
  onPageSize?: (n: number) => void;
};

/** Páginas a mostrar: primera, última y las vecinas de la actual; los
 *  huecos colapsan en «…». */
export function pageWindow(current: number, total: number): Array<number | "…"> {
  const keep = new Set([1, total, current - 1, current, current + 1]);
  const out: Array<number | "…"> = [];
  for (let p = 1; p <= total; p++) {
    if (keep.has(p)) out.push(p);
    else if (out[out.length - 1] !== "…") out.push("…");
  }
  return out;
}

export function Pager({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPage,
  numbered = false,
  pageSizes,
  onPageSize,
}: PagerProps) {
  const sizeId = useId();
  const start = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalItems);

  if (!numbered) {
    return (
      <div className="pager">
        <span className="pager__meta" aria-live="polite">
          {totalItems === 0 ? "Sin resultados" : `Mostrando ${start}–${end} de ${totalItems}`}
        </span>
        <div className="pager__nav" role="navigation" aria-label="Paginación">
          <button
            type="button"
            className="pager__btn"
            onClick={() => onPage(currentPage - 1)}
            disabled={currentPage <= 1}
            aria-label="Página anterior"
          >
            <Icon.ChevronLeft s={12} /> Prev
          </button>
          <span className="pager__label" aria-current="page">
            {currentPage} / {Math.max(1, totalPages)}
          </span>
          <button
            type="button"
            className="pager__btn"
            onClick={() => onPage(currentPage + 1)}
            disabled={currentPage >= totalPages}
            aria-label="Página siguiente"
          >
            Next <Icon.ChevronRight s={12} />
          </button>
        </div>
      </div>
    );
  }

  const total = Math.max(1, totalPages);
  const go = (n: number) => {
    // Nunca emite una página fuera de rango.
    if (n >= 1 && n <= total && n !== currentPage) onPage(n);
  };

  return (
    <div className="pager">
      <span className="pager__meta">
        {totalItems === 0 ? "Sin resultados" : `${start}–${end} de ${totalItems}`}
      </span>
      <div className="pager__nav">
        <nav className="pager__nav" aria-label="Paginación">
          <button
            type="button"
            className="pager__btn"
            onClick={() => go(currentPage - 1)}
            disabled={currentPage <= 1}
            aria-label="Página anterior"
          >
            <Icon.ChevronLeft s={12} /> Anterior
          </button>
          {pageWindow(currentPage, total).map((p, i) =>
            p === "…" ? (
              <span key={`gap-${i}`} className="pager__label" aria-hidden="true">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                className="pager__btn"
                aria-current={p === currentPage ? "page" : undefined}
                aria-label={`Página ${p}`}
                onClick={() => go(p)}
              >
                {p}
              </button>
            )
          )}
          <button
            type="button"
            className="pager__btn"
            onClick={() => go(currentPage + 1)}
            disabled={currentPage >= total}
            aria-label="Página siguiente"
          >
            Siguiente <Icon.ChevronRight s={12} />
          </button>
        </nav>
        {pageSizes && onPageSize && (
          <>
            <label htmlFor={sizeId} className="sr-only">
              Filas por página
            </label>
            <select
              id={sizeId}
              className="select select--sm"
              value={pageSize}
              onChange={(e) => onPageSize(Number(e.target.value))}
            >
              {pageSizes.map((s) => (
                <option key={s} value={s}>
                  {s} por página
                </option>
              ))}
            </select>
          </>
        )}
      </div>
    </div>
  );
}
